const flow = require('lodash/fp/flow');
const merge = require('lodash/merge');
const { format, subMonths } = require('date-fns');
const parseRequestMetaData = require('../../utils/parse-request-meta-data');
const { decode } = require('base-64');

const VALUE_MOVEMENT_MONTH_RANGE = 6;
const DATE_FORMAT = 'YYYY-MM-DD';

function getQS(parsedMetaData) {
  return {
    pfRelId: parsedMetaData.portfolio.pfRelId,
    branchCode: parsedMetaData.portfolio.branchCode,
  };
}

function apiFactory({
  baseUrl,
  requestHandler,
}) {
  const headers = {
    'Content-Type': 'application/json',
    'X-Branch-Code': 'APAC'
  };
  const reqOpts = { headers, method: 'POST' };

  const requestArray = flow(
    requestHandler,
    x => x.catch(e => {
      if (e.statusCode === 404) return [];
      throw e;
    })
  );

  function getUrl(endpoint) {
    return `${baseUrl}${endpoint}`;
  }

  return {
    getActPwdAndRegId(deviceCode, isNTB, authCode, metaData) {
      const url = getUrl('generateActPwdAndRegId');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);

      if (authCode == 'null') {
        authCode = null;
      }
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: deviceCode,
        isNTB: isNTB,
        authCode: authCode,
        channel: parsedMetaData.channel
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    generateServerEphPubKey(clientEphimeralPubKey, registrationID, deviceCode, metaData) {
      const url = getUrl('generateServerEphPubKey');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        clientEphimeralPubKey: clientEphimeralPubKey,
        registrationID: registrationID,
        deviceCode: deviceCode
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    generateServerEvidenceMessage(clientEvidenceMessage, registrationID, deviceCode, metaData) {
      const url = getUrl('generateServerEvidenceMessage');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        clientEvidenceMessage: clientEvidenceMessage,
        registrationID: registrationID,
        deviceCode: deviceCode
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    activateDevice(deviceCode, deviceCodeTwoFactorValue, registrationID, isReinstallationFlag, metaData) {
      const url = getUrl('activateDevice');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: deviceCode,
        registrationID: registrationID,
        deviceCodeTwoFactorValue: deviceCodeTwoFactorValue,
        isReinstallationFlag: isReinstallationFlag
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    verifyDeviceSignature(deviceCode, isNTB, signature, registrationID, metaData) {
      const url = getUrl('verifyDeviceSignature');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: deviceCode,
        isNTB: isNTB,
        signature: signature,
        registrationID: registrationID
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    verifyOTP(deviceCode, oneTimePassword, metaData) {
      const url = getUrl('verifyOTP');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        oneTimePassword: oneTimePassword,
        deviceCode: deviceCode,
        channel: parsedMetaData.channel
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    handleSecondFactorStatus(deviceCode, metaData) {
      const url = getUrl('handleSecondFactorStatus');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: deviceCode,
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    getSecondFactorAuthServerTime(metaData) {
      const url = getUrl('getSecondFactorAuthServerTime');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {};

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    setUserLoginAlias(metaData) {
      const url = getUrl('setUserLoginAlias');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    updateActivationStatus(deviceCode, isNTB, vascoStatus, isRegistrationSkipped, emailId, language, metaData) {
      const url = getUrl('updateActivationStatus');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: deviceCode,
        isNTB: isNTB,
        vascoStatus: vascoStatus,
        isRegistrationSkipped: isRegistrationSkipped,
        channel: parsedMetaData.channel,
        emailId: emailId,
        language: language,
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    deactivateSecondFactor(deviceCode, forgetPinMessage, serialInstance, metaData) {
      const url = getUrl('deactivateSecondFactor');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        forgetPinMessage: forgetPinMessage,
        serialInstance: serialInstance,
        deviceCode: deviceCode
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    auditSecondfactorEventLog(deviceCode, logMessage, metaData) {
      const url = getUrl('auditSecondFactorEventLog');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const decodedLogMessage = decode(logMessage);
      const body = {
        userId: parsedMetaData.qs.userId,
        logMessage: decodedLogMessage,
        deviceCode: deviceCode
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    registerViaSMS(inputs, metaData) {
      const url = getUrl('registerViaSMS');
      const inputParams = inputs.split("#@#");
      const reqOpts = { headers, method: 'POST', json: true };
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: inputParams[0],
        deviceId: [inputParams[1]]
      };
      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    validateSMSAuthCode(inputs, metaData) {
      const url = getUrl('validateSMSAuthCode');
      const inputParams = inputs.split("#@#");
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: inputParams[0],
        isSMSAuthCode: inputParams[1],
        authCodeSid: inputParams[2],
        authCode: inputParams[3]
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },
    handleSecondFactorStatusAndAuthServerTime(deviceCode, serverTimeValue, metaData) {
      let urls = [];
      console.log("inside handleSecondFactorStatusAndAuthServerTime", serverTimeValue);
      if (serverTimeValue == "undefined" || !serverTimeValue) {
        console.log("Inside serverTimeValue == undefined || !serverTimeValue");
        urls = [
          getUrl('handleSecondFactorStatus'),
          getUrl('getSecondFactorAuthServerTime')
        ];
      } else {
        console.log("Inside else block serverTimeValue == undefined || !serverTimeValue");
        urls = [
          getUrl('handleSecondFactorStatus'),
        ];
      }

      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: deviceCode,
      };

      return Promise.all(urls.map(url => requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ))
      ))
    },

    fetchSecondFactorStatusDetails(inputs, metaData) {
      const url = getUrl('handleSecondFactorStatus');
      const inputParams = inputs.split("#@#");
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: inputParams[0],
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },

    deactivateDevice(deviceCode, metaData) {
      const url = getUrl('deactivateSecondFactor');
      const reqOpts = { headers, method: 'POST', json: true }
      const parsedMetaData = parseRequestMetaData(metaData);
      const body = {
        userId: parsedMetaData.qs.userId,
        deviceCode: deviceCode
      };

      return requestHandler(merge(
        {},
        reqOpts,
        { url, body },
        parsedMetaData
      ));
    },


  };
}

module.exports = apiFactory;
